import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Box, Collapse, Drawer, Stack, Typography } from "@mui/material";
import DashboardIcon from "@mui/icons-material/SpaceDashboardRounded";
import WorkIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AlgoIcon from "@mui/icons-material/AutoGraphRounded";
import HistoryIcon from "@mui/icons-material/ReceiptLongRounded";
import McxIcon from "@mui/icons-material/TerrainRounded";
import ScienceIcon from "@mui/icons-material/ScienceRounded";
import LibraryIcon from "@mui/icons-material/LibraryBooksRounded";
import TrendIcon from "@mui/icons-material/TrendingUpRounded";
import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import BoltIcon from "@mui/icons-material/BoltRounded";
import ScreenIcon from "@mui/icons-material/MonitorHeartRounded";
import SettingsIcon from "@mui/icons-material/SettingsRounded";
import AllInclusiveIcon from "@mui/icons-material/AllInclusiveRounded";
import SwingIcon from "@mui/icons-material/WavesRounded";
import ProfitIcon from "@mui/icons-material/SavingsRounded";
import IndexIcon from "@mui/icons-material/AccountBalanceRounded";
import GavelIcon from "@mui/icons-material/GavelRounded";
import HubIcon from "@mui/icons-material/HubRounded";
import JarvisIcon from "@mui/icons-material/RecordVoiceOverRounded";
import GlobeIcon from "@mui/icons-material/PublicRounded";

import type { BotMode, BrokerInfo } from "../api";
import { PROFILE } from "../profile";

// Keys must match prebuilt.PREBUILT_CATALOG — the detail page looks the
// strategy up by key from the live decisions.
/**
 * Strategies shown under the Strategies nav.
 *
 * Only what the engine actually loads. ``state`` mirrors the backend split:
 *
 *   live    prebuilt.ACTIVE_BY_DEFAULT — scored and allowed to take a trade
 *   shadow  prebuilt.SHADOW_BY_DEFAULT — scored every scan and shown here, but
 *           OptionEngine.observe_only stops it ever being the fired signal
 *   off     loaded by no set; would render dimmed
 *
 * The distinction between `live` and `shadow` is the one that must not blur: a
 * shadow row shows a real, current opinion on live ticks, and it is not backed
 * by a position and never will be until someone promotes it. Rendering the two
 * the same way would make an unvalidated strategy look like it is trading.
 *
 * Nothing is listed at all as of 09-Aug-2026. Whatever is added back here must
 * match prebuilt.ACTIVE_BY_DEFAULT and SHADOW_BY_DEFAULT: a row that links to
 * a key the engine no longer loads renders a page with no signals on it, which
 * looks like a bug rather than a retirement.
 */
// EMPTY as of 09-Aug-2026: the entire Renko family was retired on the backend
// (prebuilt.RETIRED) and nothing was promoted in its place, so there is nothing
// live and nothing shadowed to link to. The nav renders a short explanation
// instead of an empty section, because a silently blank list reads like a
// loading failure rather than a deliberate state.
const STRATEGIES: { key: string; label: string; state: "live" | "shadow" | "off" }[] = [];

const linkSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.4,
  px: 1.5,
  py: 1.25,
  borderRadius: "10px",
  color: "var(--text)",
  textDecoration: "none",
  fontSize: ".9rem",
  fontWeight: 600,
  border: "1px solid transparent",
  transition: "background 160ms ease, transform 160ms ease",
  "&:hover": { background: "#101825", transform: "translateX(2px)" },
  "&.active": {
    color: "var(--text-2)",
    fontWeight: 800,
    background: "linear-gradient(95deg,#122b47,#0f1c2c)",
    borderColor: "#1d3a5c",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.08), 0 2px 10px rgba(0,0,0,.4)",
  },
  "& svg": { fontSize: 19, opacity: 0.9 },
};

interface SidebarProps {
  /** Phone drawer state. Ignored by the desktop rail, which is always shown. */
  open?: boolean;
  onClose?: () => void;
  mode: BotMode;
  onStart: () => void;
  onStop: () => void;
  broker?: BrokerInfo;
  /** Server runs only CAS Engine and Web + Elliott: hide the other strategy pages. */
  detached?: boolean;
}

export default function Sidebar({ open = false, onClose, mode, onStart, onStop, broker, detached = false }: SidebarProps) {
  const [openAlgos, setOpenAlgos] = useState(true);
  const isLive = mode === "LIVE";
  // Never claim real execution unless the adapter actually exposes an order
  // gateway — this label is the clearest signal of whether money is at risk.
  //
  // An offline feed gets its own wording rather than the paper-fills line.
  // "simulated fills" is true of it but says the wrong thing: the fills are
  // hypothetical because nothing is trading at all, not because orders are
  // being paper-filled against a live market. The adapter's own `note` carries
  // the detail (which bar, how many sessions), so it wins where present.
  const blurb = broker?.offline
    ? broker.note ??
      `${broker.name} — real prices, frozen. No live market; nothing is trading.`
    : broker?.live_orders
      ? `${broker.name} — orders go to the exchange.`
      : `${broker?.name ?? "No broker"} — simulated fills, nothing sent to an exchange.`;

  // The nav content, rendered twice: as a permanent rail on desktop and
  // inside a Drawer on phones. Extracted rather than duplicated so the two
  // can never drift apart.
  const content = (
    <>
      {/* brand */}
      <Stack direction="row" alignItems="center" gap={1.2} sx={{ px: 0.5, pb: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(145deg,#1f6feb,#0b3a86)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,.35), 0 4px 12px rgba(31,111,235,.45)",
          }}
        >
          <BoltIcon sx={{ fontSize: 21, color: "#fff" }} />
        </Box>
        <Typography
          sx={{ fontSize: "1.22rem", fontWeight: 900, letterSpacing: "-.03em" }}
        >
          Option<span style={{ color: "var(--blue)" }}>Engine</span>
        </Typography>
      </Stack>

      {/* nav */}
      <Stack gap={0.3}>
        <Box component={NavLink} to="/" end sx={linkSx}>
          <DashboardIcon /> Dashboard
        </Box>
        <Box component={NavLink} to="/positions" sx={linkSx}>
          <WorkIcon /> Positions
        </Box>
        <Box component={NavLink} to="/jarvis" sx={linkSx}>
          <JarvisIcon /> Jarvis
        </Box>
        <Box component={NavLink} to="/macro-desk" sx={linkSx}>
          <GlobeIcon /> Macro Desk
        </Box>

        {!detached && (<>
        <Box
          onClick={() => setOpenAlgos((v) => !v)}
          sx={{ ...linkSx, cursor: "pointer", userSelect: "none" }}
        >
          <AlgoIcon /> Strategies
          <ExpandIcon
            sx={{
              ml: "auto",
              fontSize: 18,
              transition: "transform 180ms ease",
              transform: openAlgos ? "rotate(180deg)" : "none",
            }}
          />
        </Box>
        <Collapse in={openAlgos}>
          <Stack gap={0.2} sx={{ pl: 3.6, py: 0.3 }}>
            {STRATEGIES.length === 0 && (
              <Typography
                sx={{ fontSize: ".72rem", color: "var(--muted)", py: 0.8, pr: 1.5 }}
              >
                No strategy is live. The Renko family was retired after testing;
                nothing has been promoted in its place, so the bot is scanning
                but will not trade.
              </Typography>
            )}
            {STRATEGIES.map((s) => (
              <Box
                key={s.key}
                component={NavLink}
                to={`/strategies/${s.key}`}
                sx={{
                  ...linkSx,
                  py: 0.8,
                  fontSize: ".8rem",
                  fontWeight: s.state === "live" ? 700 : 500,
                  color: s.state === "off" ? "var(--muted)" : "var(--text)",
                  opacity: s.state === "live" ? 1 : s.state === "shadow" ? 0.78 : 0.5,
                  "&.active": {
                    color: "var(--blue)",
                    background: "transparent",
                    fontWeight: 700,
                  },
                }}
              >
                {s.label}
                {s.state === "live" ? (
                  <span className="dot-live" style={{ marginLeft: "auto" }} />
                ) : (
                  <span
                    title={
                      s.state === "shadow"
                        ? "Scored live every scan, but cannot take a trade"
                        : "Not loaded"
                    }
                    style={{
                      marginLeft: "auto",
                      fontSize: ".56rem",
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      color: s.state === "shadow" ? "var(--amber, #d9a441)" : "var(--muted)",
                    }}
                  >
                    {s.state === "shadow" ? "WATCH" : "OFF"}
                  </span>
                )}
              </Box>
            ))}
          </Stack>
        </Collapse>
        </>)}

        {/* Placed directly under Positions: on an expiry afternoon this is
            the page that decides what settles, so it must not be buried in
            the strategy list. */}
        <Box component={NavLink} to="/cas" sx={linkSx}>
          <GavelIcon /> CAS Engine
        </Box>
        {!detached && (
        <Box component={NavLink} to="/blast" sx={linkSx}>
          <BoltIcon /> NIFTY Blast
        </Box>
        )}
        <Box component={NavLink} to="/web" sx={linkSx}>
          <HubIcon /> Web + Elliott
        </Box>
        {!detached && (<>
        <Box component={NavLink} to="/mcx" sx={linkSx}>
          <McxIcon /> MCX Digger
        </Box>
        <Box component={NavLink} to="/prebuilt" sx={linkSx}>
          <AlgoIcon /> Pre-Built Algos
        </Box>
        <Box component={NavLink} to="/master-options" sx={linkSx}>
          <LibraryIcon /> MasterOption-Strategy
        </Box>
        <Box component={NavLink} to="/trend-master" sx={linkSx}>
          <TrendIcon /> TrendMaster
        </Box>
        <Box component={NavLink} to="/stock-trend-master" sx={linkSx}>
          <WorkIcon /> Stock Trend Master
        </Box>
        <Box component={NavLink} to="/stock-playbook" sx={linkSx}>
          <LibraryIcon /> Stock Playbook
        </Box>
        <Box component={NavLink} to="/index-playbook" sx={linkSx}>
          <IndexIcon /> Index Playbook
        </Box>
        <Box component={NavLink} to="/swing-trade" sx={linkSx}>
          <SwingIcon /> Swing Trade
        </Box>
        <Box component={NavLink} to="/master-swing" sx={linkSx}>
          <BoltIcon /> Master Swing
        </Box>
        <Box component={NavLink} to="/count-back-line" sx={linkSx}>
          <SwingIcon /> Count-Back Line
        </Box>
        <Box component={NavLink} to="/intraday-swing" sx={linkSx}>
          <SwingIcon /> IntradaySwing
        </Box>
        <Box component={NavLink} to="/master-profit" sx={linkSx}>
          <ProfitIcon /> Master-Profit Strategy
        </Box>
        <Box component={NavLink} to="/master-scalping" sx={linkSx}>
          <BoltIcon /> MasterScalping
        </Box>
        </>)}
        <Box component={NavLink} to="/backtest" sx={linkSx}>
          <ScienceIcon /> Backtest
        </Box>
        <Box component={NavLink} to="/history" sx={linkSx}>
          <HistoryIcon /> Trade History
        </Box>
      </Stack>

      {/* trading-mode card */}
      <Box
        sx={{
          mt: "auto",
          borderRadius: "14px",
          p: 1.75,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(150deg,#2563eb 0%,#3b82f6 48%,#6d3bf6 100%)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,.4), 0 10px 26px rgba(37,99,235,.42)",
        }}
      >
        <Stack direction="row" gap={0.8} sx={{ mb: 1.2 }}>
          {[<AlgoIcon key="a" />, <ScreenIcon key="b" />].map((icon, i) => (
            <Box
              key={i}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,.18)",
                border: "1px solid rgba(255,255,255,.25)",
                "& svg": { fontSize: 17, color: "#fff" },
              }}
            >
              {icon}
            </Box>
          ))}
        </Stack>

        <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#fff" }}>
          Trading Mode
        </Typography>
        <Typography
          sx={{ fontSize: ".72rem", color: "rgba(255,255,255,.8)", mt: 0.4, mb: 1.4 }}
        >
          {blurb}
        </Typography>

        <Stack
          direction="row"
          sx={{
            background: "rgba(9,18,40,.42)",
            borderRadius: "10px",
            p: 0.5,
            gap: 0.5,
          }}
        >
          {(
            [
              { label: "Live", on: isLive, act: onStart },
              { label: "Paused", on: !isLive, act: onStop },
            ] as const
          ).map((b) => (
            <Box
              key={b.label}
              onClick={b.act}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 0.85,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: ".8rem",
                fontWeight: 800,
                color: b.on ? "#0b1a33" : "rgba(255,255,255,.85)",
                background: b.on ? "#fff" : "transparent",
                boxShadow: b.on ? "0 2px 8px rgba(0,0,0,.3)" : "none",
                transition: "all 160ms ease",
              }}
            >
              {b.label}
            </Box>
          ))}
        </Stack>
      </Box>

      {/* quota card */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.2}
        className="panel3d"
        sx={{ p: 1.5 }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(145deg,#2a2140,#171227)",
            border: "1px solid #3a2f57",
          }}
        >
          <BoltIcon sx={{ fontSize: 17, color: "var(--purple)" }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: ".86rem", fontWeight: 800 }}>
            Unlimited
          </Typography>
          <Typography sx={{ fontSize: ".68rem", color: "var(--muted)" }}>
            Scans &amp; Backtests
          </Typography>
        </Box>
        <AllInclusiveIcon
          sx={{ ml: "auto", fontSize: 18, color: "var(--muted)" }}
        />
      </Stack>

      {/* user row */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.2}
        sx={{ px: 0.5, pt: 0.5, borderTop: "1px solid var(--border)" }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontSize: ".82rem",
            fontWeight: 800,
            color: "#04121f",
            background: "linear-gradient(145deg,#7fe3a6,#00e676)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)",
          }}
        >
          {PROFILE.initial}
        </Box>
        <Typography sx={{ fontSize: ".86rem", fontWeight: 700 }}>
          {PROFILE.shortName}
        </Typography>
        <SettingsIcon
          sx={{
            ml: "auto",
            fontSize: 18,
            color: "var(--muted)",
            cursor: "pointer",
            "&:hover": { color: "var(--text-2)", transform: "rotate(45deg)" },
            transition: "all 200ms ease",
          }}
        />
      </Stack>
    </>
  );

  return (
    <>
      {/* desktop rail */}
      <Box
        component="aside"
        sx={{
        width: 262,
        flex: "0 0 262px",
        // Hidden on phones and tablets; the Drawer below takes over there.
        display: { xs: "none", lg: "flex" },
        height: "100vh",
        position: "sticky",
        top: 0,
        flexDirection: "column",
        gap: 1.5,
        p: 1.75,
        overflowY: "auto",
        background: "linear-gradient(180deg,#0c1119 0%,#080c12 100%)",
        borderRight: "1px solid var(--border)",
        boxShadow: "18px 0 40px rgba(0,0,0,.45)",
        zIndex: 3,
      }}
      >
        {content}
      </Box>

      {/* phone drawer. The rail is 262px of a 375px screen, which left the
          dashboard 113px and pushed the page to 791px wide -- 416px of
          horizontal overflow. Off-canvas below lg, opened from the top bar. */}
      <Drawer
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", lg: "none" } }}
        PaperProps={{
          sx: {
            width: 268,
            p: 1.75,
            gap: 1.5,
            display: "flex",
            flexDirection: "column",
            border: "none",
            background: "linear-gradient(180deg,#0c1119 0%,#080c12 100%)",
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
}
